import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Users, Wrench, X } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { DatePickerInput } from "shared/components/DatePickerInput/DatePickerInput";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession, useUser } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import {
  INTERVENTION_TYPES,
  INTERVENTION_STATUSES,
  InterventionDTO,
  InterventionStatus,
  InterventionType,
} from "../intervention/interventionConstants";

interface AddInterventionModalProps {
  isOpen: boolean;
  ticketId: number;
  intervention?: InterventionDTO | null;
  /** Ticket-level supplier — used to prefill on new interventions */
  ticketSupplier?: { id: number; name: string } | null;
  onClose: () => void;
  onSaved: () => void;
}

export const AddInterventionModal: React.FC<AddInterventionModalProps> = ({
  isOpen,
  ticketId,
  intervention,
  ticketSupplier,
  onClose,
  onSaved,
}) => {
  const { session } = useSession();
  const { user } = useUser();

  const [type, setType] = useState<InterventionType>(INTERVENTION_TYPES[0].value);
  const [status, setStatus] = useState<InterventionStatus>(
    INTERVENTION_STATUSES[0].value,
  );
  const [plannedDate, setPlannedDate] = useState<string>(""); // yyyy-MM-dd
  const [plannedTime, setPlannedTime] = useState<string>("09:00"); // HH:mm
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const [description, setDescription] = useState<string>("");
  const [notesForAttendees, setNotesForAttendees] = useState<string>("");
  const [sendInvite, setSendInvite] = useState<boolean>(true);
  const [tenantAccessRequired, setTenantAccessRequired] = useState<boolean>(false);
  const [supplier, setSupplier] = useState<{ id: number; name: string } | null>(
    null,
  );
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [extraInvitees, setExtraInvitees] = useState<
    Array<{ id: number; name: string; email: string | null }>
  >([]);
  const [inviteePickerOpen, setInviteePickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (intervention) {
      setType(intervention.type);
      setStatus(intervention.status);
      // Split the stored UTC ISO into local date + local time for the inputs.
      // Browser's local TZ is assumed to be Brussels for these users; the
      // backend always interprets naive payload as Brussels.
      if (intervention.plannedDate) {
        const local = new Date(intervention.plannedDate);
        const yyyy = local.getFullYear();
        const mm = String(local.getMonth() + 1).padStart(2, "0");
        const dd = String(local.getDate()).padStart(2, "0");
        const hh = String(local.getHours()).padStart(2, "0");
        const mi = String(local.getMinutes()).padStart(2, "0");
        setPlannedDate(`${yyyy}-${mm}-${dd}`);
        setPlannedTime(`${hh}:${mi}`);
      } else {
        setPlannedDate("");
        setPlannedTime("09:00");
      }
      setDurationMinutes(intervention.durationMinutes ?? 60);
      setDescription(intervention.description ?? "");
      setNotesForAttendees(intervention.notesForAttendees ?? "");
      setSendInvite(intervention.sendInvite ?? true);
      setTenantAccessRequired(intervention.tenantAccessRequired ?? false);
      setSupplier(
        intervention.supplier
          ? { id: intervention.supplier.id, name: intervention.supplier.name }
          : null,
      );
      // Hydrate extra invitees by fetching each contact (best-effort).
      const ids = intervention.additionalInviteeContactIds || [];
      if (ids.length === 0) {
        setExtraInvitees([]);
      } else if (session) {
        Promise.all(
          ids.map(async (id) => {
            try {
              const r = await fetchWithAuth(
                `${API_URL}/contacts/${id}`,
                { method: "GET" },
                session,
              );
              if (!r.ok) return null;
              const c = await r.json();
              return {
                id: Number(c.id),
                name:
                  `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() ||
                  c.email ||
                  `Contact ${c.id}`,
                email: c.email ?? null,
              };
            } catch {
              return null;
            }
          }),
        ).then((rows) => {
          setExtraInvitees(
            rows.filter((r): r is NonNullable<typeof r> => r !== null),
          );
        });
      }
    } else {
      setType(INTERVENTION_TYPES[0].value);
      setStatus(INTERVENTION_STATUSES[0].value);
      setPlannedDate("");
      setPlannedTime("09:00");
      setDurationMinutes(60);
      setDescription("");
      setNotesForAttendees("");
      setSendInvite(true);
      setTenantAccessRequired(false);
      setExtraInvitees([]);
      // Prefill supplier from ticket if one is set, so users don't have to
      // re-select the same leverancier on every intervention.
      setSupplier(
        ticketSupplier
          ? { id: ticketSupplier.id, name: ticketSupplier.name }
          : null,
      );
    }
  }, [intervention, isOpen, ticketSupplier, session]);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const isEdit = !!intervention;
      const url = isEdit
        ? `${API_URL}/helpdesk/interventions/${intervention!.id}`
        : `${API_URL}/helpdesk/ticket/${ticketId}/interventions`;
      // Send a naive datetime "yyyy-MM-ddTHH:mm:00" so the backend can
      // interpret it as Europe/Brussels local time. No browser-TZ surprises.
      const naiveDateTime = plannedDate
        ? `${plannedDate}T${(plannedTime || "09:00")}:00`
        : null;
      const payload = {
        type,
        status,
        plannedDate: naiveDateTime,
        durationMinutes,
        description: description.trim() || null,
        notesForAttendees: notesForAttendees.trim() || null,
        sendInvite,
        tenantAccessRequired,
        additionalInviteeContactIds: extraInvitees.map((i) => i.id),
        supplierId: supplier?.id ?? null,
        ...(isEdit ? {} : { userId: user?.id }),
      };

      const response = await fetchWithAuth(
        url,
        {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        session,
      );

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `Request failed: ${response.status}`);
      }

      toast.success(
        isEdit ? "Interventie bijgewerkt" : "Interventie toegevoegd",
      );
      onSaved();
      onClose();
    } catch (error) {
      console.error("[INTERVENTION] Save failed:", error);
      toast.error("Opslaan van interventie mislukt");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onOpenChange={(open) => !open && onClose()}
        // modal={false} disables Radix's focus-trap + aria-hidden on external
        // elements so the supplier UniversalPickerModal (rendered in its own
        // portal at document.body) stays interactive when stacked on top.
        modal={false}
      >
        <DialogContent
          className="max-w-xl"
          // With modal={false}, every interaction in the supplier picker is
          // "outside" this content — preventDefault keeps the parent open so
          // we don't lose the user's draft when they tap a supplier.
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-neutral-primary flex items-center gap-2">
              <Wrench size={22} className="text-moss-primary" />
              {intervention ? "Interventie bewerken" : "Nieuwe interventie"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="intervention-type">Type</Label>
                <Select
                  value={type}
                  onValueChange={(v) => setType(v as InterventionType)}
                >
                  <SelectTrigger id="intervention-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[240px]">
                    {INTERVENTION_TYPES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="intervention-status">Status</Label>
                <Select
                  value={status}
                  onValueChange={(v) => setStatus(v as InterventionStatus)}
                >
                  <SelectTrigger id="intervention-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {INTERVENTION_STATUSES.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto]">
              <div className="space-y-2">
                <Label htmlFor="intervention-date">Geplande datum</Label>
                <DatePickerInput
                  id="intervention-date"
                  value={plannedDate}
                  onChange={(v) => setPlannedDate(v)}
                  placeholder="dd/mm/jjjj"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="intervention-time">Tijdstip</Label>
                <Input
                  id="intervention-time"
                  type="time"
                  step={300}
                  value={plannedTime}
                  onChange={(e) => setPlannedTime(e.target.value)}
                  className="w-[120px]"
                  disabled={!plannedDate}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="intervention-duration">Duur (min)</Label>
                <Input
                  id="intervention-duration"
                  type="number"
                  min={15}
                  max={480}
                  step={15}
                  value={durationMinutes}
                  onChange={(e) => {
                    const next = parseInt(e.target.value, 10);
                    if (!Number.isNaN(next)) setDurationMinutes(next);
                  }}
                  className="w-[100px]"
                  disabled={!plannedDate}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Leverancier</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 justify-start"
                  onClick={() => setSupplierPickerOpen(true)}
                >
                  {supplier?.name || "Selecteer leverancier"}
                </Button>
                {supplier && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setSupplier(null)}
                    aria-label="Leverancier verwijderen"
                  >
                    <X size={16} />
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="intervention-description">Interne omschrijving</Label>
              <Textarea
                id="intervention-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Interne notitie (niet in agenda-uitnodiging)..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="intervention-notes-attendees">
                Notitie voor deelnemers
              </Label>
              <Textarea
                id="intervention-notes-attendees"
                value={notesForAttendees}
                onChange={(e) => setNotesForAttendees(e.target.value)}
                placeholder="Bijkomende info die in de agenda-uitnodiging meegaat..."
                rows={2}
              />
            </div>

            {/* Toggles */}
            <div className="border-border space-y-3 rounded-md border bg-neutral-50 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label
                    htmlFor="toggle-send-invite"
                    className="text-neutral-primary text-sm font-medium"
                  >
                    Plan in Leo's agenda
                  </Label>
                  <p className="text-neutral-secondary-mid text-xs">
                    Stuur een agenda-uitnodiging op de geplande datum.
                  </p>
                </div>
                <Switch
                  id="toggle-send-invite"
                  checked={sendInvite}
                  onCheckedChange={setSendInvite}
                  disabled={!plannedDate}
                />
              </div>

              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label
                    htmlFor="toggle-tenant-access"
                    className="text-neutral-primary text-sm font-medium"
                  >
                    Huurder moet toegang verschaffen
                  </Label>
                  <p className="text-neutral-secondary-mid text-xs">
                    Voegt de huidige huurder van deze unit toe als deelnemer.
                  </p>
                </div>
                <Switch
                  id="toggle-tenant-access"
                  checked={tenantAccessRequired}
                  onCheckedChange={setTenantAccessRequired}
                  disabled={!sendInvite || !plannedDate}
                />
              </div>
            </div>

            {/* Extra invitees */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <Users size={14} className="text-neutral-secondary-mid" />
                  Extra deelnemers
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setInviteePickerOpen(true)}
                  disabled={!sendInvite || !plannedDate}
                >
                  <Plus size={14} className="mr-1" />
                  Voeg deelnemer toe
                </Button>
              </div>
              {extraInvitees.length === 0 ? (
                <p className="text-neutral-secondary-mid text-xs italic">
                  Geen extra deelnemers. Voeg contacten toe die de
                  agenda-uitnodiging moeten ontvangen.
                </p>
              ) : (
                <ul className="flex flex-wrap gap-2">
                  {extraInvitees.map((invitee) => (
                    <li
                      key={invitee.id}
                      className="bg-moss-primary/10 border-moss-primary/20 text-moss-primary flex items-center gap-1.5 rounded-full border py-1 pr-1 pl-3 text-xs"
                      title={invitee.email || ""}
                    >
                      <span className="font-medium">{invitee.name}</span>
                      {!invitee.email && (
                        <span className="text-amber-700" title="Geen e-mailadres">
                          (geen e-mail)
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          setExtraInvitees((curr) =>
                            curr.filter((i) => i.id !== invitee.id),
                          )
                        }
                        className="hover:bg-moss-primary/20 rounded-full p-0.5"
                        aria-label={`Verwijder ${invitee.name}`}
                      >
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Annuleer
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-moss-primary hover:bg-moss-primary/90 text-white"
            >
              {submitting ? "Opslaan..." : intervention ? "Bijwerken" : "Toevoegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {supplierPickerOpen && (
        <UniversalPickerModal
          isOpen={supplierPickerOpen}
          mode="supplier"
          title="Selecteer leverancier"
          onClose={() => setSupplierPickerOpen(false)}
          onConfirm={(value: any) => {
            setSupplier(
              value ? { id: value.id, name: value.name } : null,
            );
            setSupplierPickerOpen(false);
          }}
          config={{
            allowCreateSupplier: true,
            allowRemoval: true,
          }}
        />
      )}

      {inviteePickerOpen && (
        <UniversalPickerModal
          isOpen={inviteePickerOpen}
          mode="reporter"
          title="Voeg een deelnemer toe"
          onClose={() => setInviteePickerOpen(false)}
          onConfirm={(value: any) => {
            if (value && value.id !== undefined) {
              const idNum = Number(value.id);
              if (
                Number.isFinite(idNum) &&
                !extraInvitees.some((i) => i.id === idNum)
              ) {
                const fullName =
                  `${value.firstName ?? ""} ${value.lastName ?? ""}`.trim() ||
                  value.email ||
                  `Contact ${idNum}`;
                setExtraInvitees((curr) => [
                  ...curr,
                  {
                    id: idNum,
                    name: fullName,
                    email: value.email ?? null,
                  },
                ]);
              }
            }
            setInviteePickerOpen(false);
          }}
          config={{ allowRemoval: false }}
        />
      )}
    </>
  );
};
